/**
 * A dependency that has been aimed at an issue but not saved.
 *
 * Adding one from the panel used to write an edge immediately, with Finish →
 * start and "Rigid" filled in by the database defaults — so the relationship,
 * the one thing that needs a decision, was decided for the user and usually
 * left alone. Nothing is written until they pick it; the lag and whether it
 * moves together can be set first and ride along with the insert.
 */
import { useState } from "react";
import { CornerDownRight, CornerUpRight, X } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { IssueDependencyType, PMIssue } from "@/domains/pm/types";
import type { EdgePatch } from "@/domains/pm/hooks/useStagedSchedule";
import { DependencyEditor, type DependencyDraft } from "./DependencyRow";
import { NEW_EDGE } from "./dependencyConstants";

export function DependencyDraftRow({
  candidate,
  direction,
  onCreate,
  onDiscard,
  pending = false,
}: {
  candidate: PMIssue;
  direction: "waiting" | "drives";
  /** Called once a relationship is chosen — the first complete shape. */
  onCreate: (edge: {
    dep_type: IssueDependencyType;
    lag_days: number;
    hardness: "hard" | "soft";
  }) => void;
  onDiscard: () => void;
  pending?: boolean;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const [draft, setDraft] = useState<DependencyDraft>({ dep_type: null, ...NEW_EDGE });
  const DirectionIcon = direction === "waiting" ? CornerDownRight : CornerUpRight;

  const change = (patch: EdgePatch) => {
    if (pending) return;
    const next = { ...draft, ...patch };
    setDraft(next);
    if (patch.dep_type && next.dep_type) {
      onCreate({ dep_type: next.dep_type, lag_days: next.lag_days, hardness: next.hardness });
    }
  };

  return (
    <li className="rounded-md border-l-2 border-primary" aria-busy={pending}>
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
        <DirectionIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="shrink-0 text-[11px] font-semibold tabular-nums text-primary">
          #{candidate.issue_number}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm">{candidate.title}</span>
        <button
          type="button"
          aria-label={copy.cancelDraft}
          onClick={onDiscard}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-primary"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <fieldset disabled={pending} className="min-w-0 border-0 p-0">
        <DependencyEditor
          dep={draft}
          other={candidate}
          direction={direction}
          onChange={change}
          isDraft
        />
      </fieldset>
    </li>
  );
}
